import {
    AfterViewInit,
    Component,
    computed,
    effect,
    inject,
    input,
    OnDestroy,
    OnInit,
    signal,
    untracked,
    viewChild,
} from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { UntilDestroy } from '@ngneat/until-destroy';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { FormControl, FormGroup } from '@angular/forms';
import { EcmrStatus } from '@app/shared/models/ecmr/EcmrStatus';
import { EcmrTransportType } from '@app/shared/models/ecmr/EcmrTransportType';
import { CdkDrag, CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { DateFormatService } from '@app/shared/services/date-format.service';
import { EcmrService } from '@app/core/services/ecmr-services/ecmr.service';
import { Ecmr } from '@app/shared/models/ecmr/Ecmr';
import { FilterRequest } from '@app/shared/models/ecmr/filter-request';
import { EcmrType } from '@app/shared/models/ecmr/EcmrType';
import { lastValueFrom, Subject } from 'rxjs';
import { EcmrPage } from '@app/shared/models/ecmr/EcmrPage';
import { SessionService } from '@app/core/services/client-services/session-service/session.service';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { RoutingEndpoints } from '@app/shared/models/routing/routing-endpoints';
import { EcmrDetailDialogComponent } from '@app/shared/components/ecmr/dialogs/ecmr-detail-dialog/ecmr-detail-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { EcmrShareDialogComponent } from '@app/shared/components/ecmr/dialogs/ecmr-share-dialog/ecmr-share-dialog.component';
import { TranslateService } from '@ngx-translate/core';
import { PdfExtractionStatus } from '@app/shared/models/ecmr/PdfExtractionStatus';
import { SearchStore } from '@app/stores/searchStore';
import { AppStore } from '@app/stores/appStore';
import { CommonModule } from '@angular/common';
import { MaterialModule } from '@app/shared/material/material.module';
import { TranslateModule } from '@ngx-translate/core';
import { ReactiveFormsModule } from '@angular/forms';
import { EcmrStatusComponent } from '@app/shared/components/ecmr/subcomponents/ecmr-status/ecmr-status.component';
import { LatestMotorPlatePipe } from '@app/shared/pipes/latest-motor-plate.pipe';
import { isEcmrDcaCompliant } from '@app/shared/components/ecmr/ecmr-dca.util';
import {
    ECMR_COLUMNS,
    ecmrColumnPreferences,
} from '@app/shared/components/ecmr/ecmr-table-columns';
import { TablePreferencesStore } from '@app/stores/tablePreferencesStore';

@UntilDestroy({ checkProperties: true })
@Component({
    selector: 'app-active-ecmr',
    templateUrl: './active-ecmr.component.html',
    styleUrl: './active-ecmr.component.scss',
    imports: [
        CommonModule,
        MaterialModule,
        TranslateModule,
        ReactiveFormsModule,
        EcmrStatusComponent,
        LatestMotorPlatePipe,
    ],
})
export class ActiveEcmrComponent implements OnInit, AfterViewInit, OnDestroy {
    baseRoute = input<string>();
    shouldReloadTable = input<Subject<boolean>>();
    isReadOnly = input<boolean>(false);

    private appStore = inject(AppStore);
    private searchStore = inject(SearchStore);
    private tablePreferencesStore = inject(TablePreferencesStore);

    isLoading: boolean = false;
    organizationSiteKey = this.appStore.currentOrganizationSiteKey;

    dataSource: MatTableDataSource<Ecmr> = new MatTableDataSource<Ecmr>();

    intervalId: ReturnType<typeof setInterval>;
    private paginatorReady = false;
    private pendingSearchQuery: string | null = null;

    paginator = viewChild(MatPaginator);
    sort = viewChild(MatSort);

    private columnPreferences = ecmrColumnPreferences('overview');

    // Column order (drag & drop). Which columns are visible is chosen via
    // Show/Hide Columns in the paginated-table-filter.
    columnOrder = signal<string[]>([
        'id',
        ...ECMR_COLUMNS.map(column => String(column.key)),
    ]);
    private visibleColumns = computed(() =>
        this.tablePreferencesStore.getVisibleColumns(
            this.sessionService.getSession()._key,
            this.columnPreferences.viewKey,
            this.columnPreferences.columns.map(column => String(column.key)),
            this.columnPreferences.defaultHidden
        )
    );
    displayedColumns = computed(() =>
        this.columnOrder().filter(
            column => column === 'id' || this.visibleColumns().includes(column)
        )
    );

    filterRequest: FilterRequest = {
        ecmrId: null,
        referenceId: '',
        from: '',
        to: '',
        transportType: null,
        status: null,
        licensePlate: '',
        carrierName: '',
        carrierPostCode: '',
        consigneePostCode: '',
        lastEditor: '',
        createdAt: '',
        editedAt: '',
        groupId: null,
        transportId: null,
    };

    filterFormGroup = new FormGroup({
        referenceId: new FormControl<string | null>(null),
        from: new FormControl<string | null>(null),
        to: new FormControl<string | null>(null),
        transportType: new FormControl<EcmrTransportType | null>(null),
        status: new FormControl<EcmrStatus | null>(null),
        licensePlate: new FormControl<string | null>(null),
        carrierName: new FormControl<string | null>(null),
        carrierPostCode: new FormControl<string | null>(null),
        consigneePostCode: new FormControl<string | null>(null),
        lastEditor: new FormControl<string | null>(null),
    });

    protected readonly EcmrStatus = EcmrStatus;
    protected readonly EcmrTransportType = EcmrTransportType;

    constructor(
        private ecmrService: EcmrService,
        protected dateFormatService: DateFormatService,
        private sessionService: SessionService,
        private router: Router,
        private toastr: ToastrService,
        private dialog: MatDialog,
        private translateService: TranslateService
    ) {
        // Listen to search state changes from the store
        effect(() => {
            const query = this.searchStore.searchText();
            this.applySearchFilter(query);
        });

        // Listen to organization site changes from the store
        effect(() => {
            const orgSiteKey = this.organizationSiteKey();
            if (orgSiteKey) {
                untracked(() => {
                    this.startRefreshInterval();
                });
            }
        });

        // Listen to refresh trigger
        effect(() => {
            const orgSiteKey = this.organizationSiteKey();
            if (orgSiteKey) {
                const refreshCount = this.searchStore.refreshTrigger();
                if (refreshCount > 0) {
                    untracked(() => {
                        this.loadEcmrs(orgSiteKey);
                    });
                }
            }
        });
    }

    ngOnInit() {
        const savedColumns = this.ecmrService.getDisplayedColumns();
        if (savedColumns) {
            this.columnOrder.set(savedColumns);
        }
    }

    async ngAfterViewInit() {
        // Set default sort to newest creation date first
        const sort = this.sort();
        if (sort) {
            sort.active = 'creationDate';
            sort.direction = 'desc';
        }

        this.paginatorReady = true;

        // Apply any pending search query, otherwise do the initial load now
        // that the paginator's [pageSize] input binding has been applied
        // (before this, paginator.pageSize is undefined). A constructor
        // effect may have already tried to load and been blocked by the
        // paginatorReady guard, so we kick it off here.
        if (this.pendingSearchQuery !== null) {
            this.applySearchFilter(this.pendingSearchQuery);
            this.pendingSearchQuery = null;
        } else {
            const siteKey = this.sessionService.currentOrganizationSiteKey();
            if (siteKey) {
                this.loadEcmrs(siteKey);
            }
        }

        this.shouldReloadTable()?.subscribe(result => {
            if (result) {
                this.loadEcmrs(
                    this.sessionService.currentOrganizationSiteKey()
                );
            }
        });
    }

    ngOnDestroy() {
        clearInterval(this.intervalId);
    }

    async loadEcmrs(siteKey: string) {
        // Guard: Don't load until the view (and the paginator's [pageSize]
        // input binding) is ready, otherwise paginator.pageSize is still
        // undefined and would be sent as size=undefined.
        const paginator = this.paginator();
        const sort = this.sort();
        if (!this.paginatorReady || !paginator || !sort) {
            return;
        }

        this.isLoading = true;

        try {
            const page: EcmrPage = await lastValueFrom(
                this.ecmrService.getAllEcmr(
                    this.filterRequest,
                    siteKey,
                    EcmrType.ECMR,
                    paginator.pageIndex,
                    paginator.pageSize,
                    sort.active || 'creationDate',
                    (sort.direction || 'desc').toUpperCase()
                )
            );

            this.dataSource.data = page.ecmrs;
            paginator.length = page.totalElements;
        } catch (e) {
            console.error('eCMR loading error:', e);
            this.toastr.error(
                this.translateService.instant('ECMR.error.loading_ecmr_failed')
            );
        } finally {
            this.isLoading = false;
        }
    }

    applySearchFilter(query: string) {
        // Store query for later if paginator not ready yet
        if (!this.paginatorReady) {
            this.pendingSearchQuery = query;
            return;
        }

        this.filterRequest.referenceId = query;
        this.filterRequest.from = query;
        this.filterRequest.to = query;
        this.filterRequest.carrierName = query;
        this.filterRequest.carrierPostCode = query;
        this.filterRequest.licensePlate = query;
        this.filterRequest.lastEditor = query;
        this.filterRequest.consigneePostCode = query;
        this.filterRequest.createdAt = query;
        this.filterRequest.editedAt = query;

        if (query) {
            const enumQuery = query.trim().toLowerCase();
            this.filterRequest.status =
                Object.values(EcmrStatus).find(status => {
                    const translated = this.translateService
                        .instant('ECMR.COMMON.STATUS.' + status)
                        .toLowerCase();
                    return enumQuery !== '' && translated.startsWith(enumQuery);
                }) || null;

            this.filterRequest.transportType =
                Object.values(EcmrTransportType).find(
                    type =>
                        enumQuery !== '' &&
                        type.toLowerCase().startsWith(enumQuery)
                ) || null;
        } else {
            this.filterRequest.status = null;
            this.filterRequest.transportType = null;
        }

        this.loadEcmrs(this.sessionService.currentOrganizationSiteKey());
    }

    async startRefreshInterval() {
        await this.loadEcmrs(this.sessionService.currentOrganizationSiteKey());
        clearInterval(this.intervalId);
        this.intervalId = setInterval(() => {
            this.loadEcmrs(this.sessionService.currentOrganizationSiteKey());
        }, 30 * 1000);
    }

    sortAndUpdateData() {
        this.loadEcmrs(this.sessionService.currentOrganizationSiteKey());
    }

    sortPredicate(index: number, item: CdkDrag<number>) {
        return index != 0;
    }

    signEcmr(ecmrId: string) {
        if (ecmrId) {
            this.router.navigateByUrl(
                this.baseRoute() +
                    '/' +
                    RoutingEndpoints.ecmr_editor +
                    `/${ecmrId}?action=sign`
            );
            /*this.router.navigate([
                this.baseRoute +
                    '/' +
                    RoutingEndpoints.ecmr_editor +
                    `/${ecmrId}?action=sign`,
            ]);*/
        }
    }

    editEcmr(ecmrId: string) {
        if (ecmrId) {
            this.router.navigate([
                this.baseRoute() +
                    '/' +
                    RoutingEndpoints.ecmr_editor +
                    `/${ecmrId}`,
            ]);
        }
    }

    async deleteEcmr(ecmrId: string) {
        try {
            await lastValueFrom(
                this.ecmrService.deleteEcmr(
                    ecmrId,
                    this.sessionService.currentOrganizationSiteKey()
                )
            );
            this.toastr.success(
                this.translateService.instant('ECMR.overview.delete_success')
            );
            this.loadEcmrs(this.sessionService.currentOrganizationSiteKey());
        } catch (e) {
            this.toastr.error(
                this.translateService.instant('ECMR.error.ecmr_delete_failed')
            );
        }
    }

    isDeleteButtonVisible(ecmr: Ecmr) {
        return ecmr.ecmrStatus === EcmrStatus.NEW;
    }

    /**
     * DeCA badge shows once the sender has signed - the backend leaves the
     * status at NEW until then - and the tax numbers make the consignment
     * conform.
     */
    isDcaCompliant(ecmr: Ecmr): boolean {
        return (
            ecmr.ecmrStatus !== EcmrStatus.NEW &&
            isEcmrDcaCompliant(ecmr.ecmrConsignment)
        );
    }

    shareEcmr(ecmr: Ecmr) {
        if (ecmr?.ecmrId) {
            const dialogRef = this.dialog.open(EcmrShareDialogComponent, {
                width: '75vw',
                data: {
                    ecmr: ecmr,
                },
            });
        }
    }

    //DragDrop function to move and save columns
    tableDrop(event: CdkDragDrop<string[]>) {
        const displayed = this.displayedColumns();
        const order = [...this.columnOrder()];
        const previousIndex = order.indexOf(displayed[event.previousIndex]);
        const currentIndex = order.indexOf(displayed[event.currentIndex]);
        // the id column stays first
        if (previousIndex > 0 && currentIndex > 0) {
            moveItemInArray(order, previousIndex, currentIndex);
            this.columnOrder.set(order);
            this.ecmrService.saveDisplayedColumns(order);
        }
    }

    public getTransportType(ecmr: Ecmr): EcmrTransportType | null {
        return this.ecmrService.getTransportType(ecmr);
    }

    showDetailsForEcmrAtIndex(index: number) {
        const dialogRef = this.dialog.open(EcmrDetailDialogComponent, {
            width: '80vw',
            height: '80vh',
            disableClose: false,
            data: {
                baseRoute: this.baseRoute(),
                ecmr: this.dataSource.data[index],
                isArchive: false,
                isReadOnly: this.isReadOnly(),
            },
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result) {
                this.loadEcmrs(
                    this.sessionService.currentOrganizationSiteKey()
                );
            }
        });
    }

    protected readonly PdfExtractionStatus = PdfExtractionStatus;
}
